import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'clsx'

// Actions
import { actions as payActions } from 'modules/pay/actions'

// Components
import { Spinner } from '@tracklib/kit'
import NewCardForm from 'components/pay/newcardform'
import PaymentMethods, { PaymentMethodTypeId } from 'components/pay/paymentmethods'
import PayPalForm from 'components/pay/paypalform'

// Hooks
import { useSavedPaymentMethod } from 'modules/pay/hooks'
import { useRequest } from 'modules/requests/hooks'

// Types
import type { State, PayState } from 'types'

import styles from './changepaymentmethod.css'

interface ChangePaymentMethodProps {
  title?: string
  onSuccess?: () => void
  alwaysExpanded?: boolean
  hideRemoveButton?: boolean
}

const ChangePaymentMethod = ({ title, onSuccess, hideRemoveButton, alwaysExpanded }: ChangePaymentMethodProps): JSX.Element => {
  const dispatch = useDispatch()
  const fetchAvailablePaymentMethodsRequest = useRequest('FETCH_AVAILABLE_PAYMENT_METHODS')
  const fetchProfileRequest = useRequest('FETCH_PROFILE')
  const { creditCards, paypal } = useSelector<State, PayState>(state => state.pay)
  const savedPaymentMethod = useSavedPaymentMethod()
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<PaymentMethodTypeId>('')
  const [activatedMethods, setActivatedMethods] = useState<Array<PaymentMethodTypeId>>([])

  // NOTE(Kitti): This method is needed to only load each payment method once. If unmounted/remounted too often, paypal will crash
  const setSelectedPaymentMethod = (methodId: PaymentMethodTypeId) => {
    if (!activatedMethods.includes(methodId)) {
      setActivatedMethods([...activatedMethods, methodId])
    }
    setSelectedPaymentMethodId(methodId)
  }

  useEffect(() => {
    if (!selectedPaymentMethodId && fetchProfileRequest.isDone) {
      if (savedPaymentMethod) {
        setSelectedPaymentMethod('saved')
      } else {
        setSelectedPaymentMethod('card')
      }
    }

    // If saved payment method is selected and removed
    if (!savedPaymentMethod && selectedPaymentMethodId === 'saved') {
      setSelectedPaymentMethod('card')
    }
  }, [selectedPaymentMethodId, savedPaymentMethod, fetchProfileRequest.isDone]) // eslint-disable-line

  useEffect(() => {
    dispatch(payActions.fetchAvailablePaymentMethods())
  }, []) // eslint-disable-line

  if (!fetchAvailablePaymentMethodsRequest.isDone || !fetchProfileRequest.isDone) {
    return <Spinner minHeight={300} delay={0} />
  }

  return (
    <>
      {title && <h3 className={styles.title}>{title}</h3>}
      <PaymentMethods
        selectedPaymentMethodId={selectedPaymentMethodId}
        setSelectedPaymentMethodId={setSelectedPaymentMethod}
        payPalData={paypal}
        creditCardsData={creditCards}
        hideRemoveButton={hideRemoveButton}
        alwaysExpanded={alwaysExpanded}
      />

      {creditCards && activatedMethods.includes('card') && (
        <div className={cn(selectedPaymentMethodId !== 'card' && styles.hidden)}>
          <h3 className={styles.subTitle}>Credit or debit card</h3>
          <p className={styles.description}>Add a new credit or debit card</p>
          <NewCardForm paymentMethods={[creditCards]} onSuccess={onSuccess} />
        </div>
      )}
      {paypal && activatedMethods.includes('payPal') && (
        <div className={cn(selectedPaymentMethodId !== 'payPal' && styles.hidden)}>
          <h3 className={styles.subTitle}>PayPal</h3>
          <p className={styles.description}>Add a PayPal account as payment method </p>
          <PayPalForm paymentMethods={[paypal]} onSuccess={onSuccess} />
        </div>
      )}
    </>
  )
}

export default ChangePaymentMethod
